import { Ref, isRef } from 'vue';
import { $t } from '@/plugins/i18n';

import ValidationRule from '@/definitions/interfaces/ValidationRule.i';
import ServiceSubstituteValidationRule from '@/definitions/interfaces/ServiceSubstituteValidationRule.i';

// BSNR and LANR are both 9-digit numbers
const MEDICAL_IDENTIFIER_PATTERN = /^\d{9}$/;

class Validation {
  /**
   * test a value against an array of rules
   * @param {Array} rules
   * @param {string} value
   * @return {boolean|string}
   */
  public test(rules: ValidationRule[] | ServiceSubstituteValidationRule[], value: any): boolean | string {
    let result: boolean | string = true;

    for (const i in rules) {
      if (rules[i](value) !== true) {
        result = rules[i](value);
        break;
      }
    }

    return result;
  }

  /**
   * merge multiple sets of rules together to have them combined
   * for example if a field should be a phone number but also be required
   * @param {Array} rules
   * @return {Array}
   */
  public mergeRules(...rules: any[]): any[] {
    return [].concat(...rules);
  }

  /**
   * get validation for required fields
   * @param {function|Ref|boolean} [skipValidation]
   * @return {Array}
   */
  public getRequiredValidator(skipValidation: ((value: string) => boolean) | Ref | boolean = false): ValidationRule[] {
    return [
      (value: string): boolean | string => {
        let skip;

        // skip by reference
        if (isRef(skipValidation)) {
          skip = !!skipValidation.value;
        }

        // skip by callback function
        else if (typeof skipValidation === 'function') {
          skip = skipValidation(value);
        }

        // skip by boolean
        else {
          skip = skipValidation;
        }

        return skip || !!value || $t('VALIDATION.REQUIRED');
      },
    ];
  }

  /**
   * get validation for email fields
   * @return {Array}
   */
  public getEmailValidator(): ValidationRule[] {
    return [
      (value: string): boolean | string => !!value || $t('VALIDATION.REQUIRED'),
      (value: string): boolean | string => (value || '').length <= 64 || $t('VALIDATION.EMAIL.LENGTH'),
      (value: string): boolean | string => {
        const pattern: RegExp = /^(([^<>()[\].,;:\s@"]+(\.[^<>()[\].,;:\s@"]+)*)|(".+"))@(([^<>()[\].,;:\s@"]+\.)+[^<>()[\].,;:\s@"]{2,})$/;
        return pattern.test(value) || $t('VALIDATION.EMAIL.INVALID');
      },
    ];
  }

  /**
   * get validator for password fields
   * @return {Array}
   */
  public getPasswordValidator(): ValidationRule[] {
    return [
      (value: string): boolean | string => !!value || $t('VALIDATION.REQUIRED'),
      (value: string): boolean | string => value.length >= 8 || $t('VALIDATION.PASSWORD.INVALID'),
      (value: string): boolean | string => {
        const pattern: RegExp = /^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[~!@#$%^&*\-_+=|";[\]{}()<>.,/?\\])/;
        return pattern.test(value) || $t('VALIDATION.PASSWORD.INVALID');
      },
    ];
  }

  /**
   * get validator for confirm password field
   * @param {object} formInstance
   * @param {string} instanceField
   * @return {Array}
   */
  public getConfirmPasswordValidator(formInstance: { [p: string]: string }, instanceField: string = 'password'): ValidationRule[] {
    return [
      (value: string): boolean | string => !!value || $t('VALIDATION.REQUIRED'),
      (value: string): boolean | string => (value === formInstance[instanceField] && formInstance[instanceField] !== '') || $t('VALIDATION.PASSWORD.MISMATCH'),
    ];
  }

  /**
   * get validator for numbers
   * @param {boolean} onlyPositive
   * @return {Array}
   */
  public getNumberValidator(onlyPositive: boolean = true): ValidationRule[] {
    const rules: ValidationRule[] = [];

    // validate as number
    rules.push((value: string): boolean | string => {
      const pattern: RegExp = onlyPositive ? /^\d+$/ : /^-?\d+$/;
      return pattern.test(value) || $t('VALIDATION.NUMBER.INVALID');
    });

    return rules;
  }

  /**
   * get validation for phone numbers
   * @return {Array}
   */
  public getPhoneNumberValidator(): ValidationRule[] {
    return [
      (value: string): boolean | string => {
        if (!value?.trim()) {
          return true;
        }

        const pattern: RegExp = /^[0-9]*$/;
        return pattern.test(value) || $t('VALIDATION.PHONE.INVALID');
      },
    ];
  }

  /**
   * get terms-of-service checkbox validator
   * @return {Array}
   */
  public getTosValidator(): ValidationRule[] {
    return [(value: string): boolean | string => !!value || ''];
  }

  /**
   * get accepted type file input validator
   * @param {string[]} acceptedFileTypes
   * @return {Array}
   */
  public getFileInputValidator(acceptedFileTypes: string[]): ValidationRule[] {
    // Simplest possible validator following Vuetify's pattern
    return [
      function fileTypeValidator(value: any): boolean | string {
        // Case 1: No file selected - pass validation (for optional files)
        if (!value) {
          return true;
        }

        // Case 2: File selected but without type - fail validation
        const file = value.type;
        if (!file) {
          return $t('VALIDATION.FILE_INPUT.INVALID_TYPE');
        }

        // Case 3: Check file type against accepted types
        const isAccepted = acceptedFileTypes.includes(file.toLowerCase());

        // Return true if valid, error message if invalid
        return isAccepted || $t('VALIDATION.FILE_INPUT.INVALID_TYPE');
      },
    ];
  }

  /**
   * get file size validator
   * @param {number} maxSizeInMB - maximum file size in megabytes
   * @return {Array}
   */
  public getFileSizeValidator(maxSizeInMB: number = 5): ValidationRule[] {
    const maxSizeInBytes = maxSizeInMB * 1024 * 1024;
    return [
      function fileSizeValidator(value: any): boolean | string {
        if (!value) {
          return true;
        }

        const fileSize = value.size;
        if (fileSize && fileSize > maxSizeInBytes) {
          return $t('VALIDATION.FILE_INPUT.SIZE_EXCEEDED', { maxSize: parseFloat(maxSizeInMB.toFixed(2)) });
        }

        return true;
      },
    ];
  }

  /**
   * get validator for German IBAN format (22 chars: DE + 2 check digits + 8-digit BLZ + 10-digit account).
   * Use mergeRules with getRequiredValidator() for required + format validation.
   * @return {Array}
   */
  public getGermanIbanValidator(): ValidationRule[] {
    return [
      (value: string): boolean | string => {
        if (!value || !value.trim()) {
          return true;
        }
        const clean = (value || '').replace(/\s/g, '').toUpperCase();
        if (clean.length !== 22) {
          return $t('VALIDATION.IBAN.INVALID');
        }
        if (!/^DE\d{20}$/.test(clean)) {
          return $t('VALIDATION.IBAN.INVALID');
        }
        // Mod 97 checksum (ISO 13616) - process in chunks to avoid integer overflow
        const rearranged = clean.slice(4) + clean.slice(0, 4);
        const numeric = rearranged.replace(/[A-Z]/g, (c) => String(c.charCodeAt(0) - 55));
        let remainder = numeric;
        while (remainder.length > 2) {
          const block = remainder.slice(0, 7);
          remainder = (parseInt(block, 10) % 97).toString() + remainder.slice(block.length);
        }
        return parseInt(remainder, 10) % 97 === 1 || $t('VALIDATION.IBAN.INVALID');
      },
    ];
  }

  /**
   * get validator for LANR format (9-digit number).
   * Use mergeRules with getRequiredValidator() for required + format validation.
   * @return {Array}
   */
  public getLanrValidator(): ValidationRule[] {
    return [
      (value: string): boolean | string => {
        if (!value || !value.trim()) {
          return true;
        }
        return this.isValidLanr(value) || $t('VALIDATION.LANR.INVALID');
      },
    ];
  }

  /**
   * check whether a value is a valid LANR
   * @param {string|null} [value]
   * @return {boolean}
   */
  public isValidLanr(value: string | null | undefined): boolean {
    return MEDICAL_IDENTIFIER_PATTERN.test((value || '').replace(/\s/g, ''));
  }

  /**
   * check whether a value is a valid BSNR
   * @param {string|null} [value]
   * @return {boolean}
   */
  public isValidBsnr(value: string | null | undefined): boolean {
    return MEDICAL_IDENTIFIER_PATTERN.test((value || '').replace(/\s/g, ''));
  }

  /**
   * get validator for BSNR format (9-digit number).
   * Use mergeRules with getRequiredValidator() for required + format validation.
   * @return {Array}
   */
  public getBsnrValidator(): ValidationRule[] {
    return [
      (value: string): boolean | string => {
        if (!value || !value.trim()) {
          return true;
        }
        return this.isValidBsnr(value) || $t('VALIDATION.BSNR.INVALID');
      },
    ];
  }

  /**
   * validate UUID v4 pattern
   * @param {string} testString
   * @return {boolean}
   */
  public validateUUIDPattern(testString: string): boolean {
    const _v4_regex = new RegExp(/^[0-9A-F]{8}-[0-9A-F]{4}-4[0-9A-F]{3}-[89AB][0-9A-F]{3}-[0-9A-F]{12}$/i);

    return _v4_regex.test(testString);
  }
}

export default new Validation();
